import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type ReprocessingInvoiceParams,
  type ReprocessingInvoiceResponse,
} from './types';
import { zodReprocessingInvoiceEndpointSchema } from './validations';

/**
 * Fetches an invoice that requiring reprocessing from the API based on the given process ID.
 * @param params - The parameters for fetching the reprocessing invoice.
 * @returns The reprocessing invoice data.
 * @throws Error if the API request fails.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_process_details_invoice_process__process_id__get
 */
export const fetchReprocessingInvoice = async (
  params: ReprocessingInvoiceParams,
): Promise<ReprocessingInvoiceResponse> => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET(params.processId), {
        validate: { pathSegments: zodReprocessingInvoiceEndpointSchema },
      })
      .json<ReprocessingInvoiceResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching reprocessing invoice',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(($) => $.fetchReprocessingInvoice.error404, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.fetchReprocessingInvoice.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
